/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';

import { useRetracked } from 'js/lib/retracked';

import * as actions from 'bundles/authentication/shared/actions';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';
import usePathname from 'bundles/authentication/shared/hooks/usePathname';
import HorizontalSocialSSO from 'bundles/expression-of-interest/components/authentication/HorizontalSocialSSO';
import {
  ACTION_QUERY_PARAMS,
  EMBEDDED_DEGREE_FORM_TRACKER,
  THIRD_PARTY_AUTH_SERVICES_ENABLED,
} from 'bundles/expression-of-interest/constants';
import useInitialFocusOnSso from 'bundles/expression-of-interest/hooks/useInitialFocusOnSSO';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

type Props = {};

const styles = {
  container: () => css`
    section {
      display: flex;
      padding-bottom: 12px;
    }
  `,
};

export const EoiSSO: React.FunctionComponent<Props> = () => {
  const { initialFocusOnSSO } = useInitialFocusOnSso();

  const pathname = usePathname();
  const logEvent = useRetracked();

  const onSocialSSOSuccess = (isRegistration: boolean) => {
    Instrumentation.login();

    logEvent({
      action: isRegistration
        ? EMBEDDED_DEGREE_FORM_TRACKER.actions.HANDLE_SUCCESSFUL_SIGNUP
        : EMBEDDED_DEGREE_FORM_TRACKER.actions.HANDLE_SUCCESSFUL_LOGIN,
      trackingName: EMBEDDED_DEGREE_FORM_TRACKER.name,
      trackingData: { pathname },
    });

    const redirectTo = {
      action: ACTION_QUERY_PARAMS.EMAIL_ME_INFO,
    };
    actions.onLogin(redirectTo);
  };

  const onSocialSSOError = (error: SSOError) => {
    Sentry.captureException(error);
    Instrumentation.thirdPartyError(error.service, error);
  };

  const servicesLabel = THIRD_PARTY_AUTH_SERVICES_ENABLED.reduce(
    (result, service) => ({ ...result, [service]: service.charAt(0).toUpperCase() + service.slice(1) }),
    {}
  );

  return (
    <div css={styles.container}>
      <HorizontalSocialSSO
        services={THIRD_PARTY_AUTH_SERVICES_ENABLED}
        onSuccess={onSocialSSOSuccess}
        onError={onSocialSSOError}
        toFocus={initialFocusOnSSO}
        servicesLabel={servicesLabel}
        // using fake ID here since this file will be removed soon
        trackingProductId="fakeDegreeID"
      />
    </div>
  );
};

export default EoiSSO;
